// src/pages/TestEvidence.js
// TestScoreComputationPage.js
import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  Card,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Chip,
  TextField,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  setEvaluationResults,
  setTrustAttributes,
  setComputedScores,
  setTestScoreData,
  setLoading as setTestScoreLoading,
  setError as setTestScoreError,
} from "../store/slices/testScoreSlice";
import SearchIcon from "@mui/icons-material/Search";
import axios from "axios";
import ProjectRequired from "../components/ProjectRequired";
import { ReactComponent as DownloadSVG } from "../asserts/logo/download.svg";

// Export libs
import { saveAs } from "file-saver";
import {
  Document,
  Packer,
  Paragraph,
  Table as DocxTable,
  TableRow as DocxTableRow,
  TableCell as DocxTableCell,
  TextRun,
} from "docx";
import jsPDF from "jspdf";

const baseUrl = process.env.REACT_APP_API_URL || "https://validaite.io/api";

/**
 * Compact Circular Gauge used in the redesigned Trust Matrix block.
 * - percentage: number 0-100 (comes from backend)
 * - size: diameter in px (default 140)
 */
function CompactCircularGauge({ percentage = 0, size = 140 }) {
  const radius = (size - 20) / 2; // keep stroke inside viewBox
  const cx = size / 2;
  const cy = size / 2;
  const circumference = 2 * Math.PI * radius;
  const progress = (percentage / 100) * circumference;

  const color =
    percentage < 50 ? "#f44336" : percentage < 75 ? "#ff9800" : "#4CAF50";

  return (
    <Box sx={{ position: "relative", width: size, height: size }}>
      <svg width={size} height={size} style={{ transform: "rotate(-90deg)" }}>
        <circle
          cx={cx}
          cy={cy}
          r={radius}
          fill="none"
          stroke="#e0e0e0"
          strokeWidth="10"
        />
        <circle
          cx={cx}
          cy={cy}
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth="10"
          strokeDasharray={circumference}
          strokeDashoffset={circumference - progress}
          strokeLinecap="round"
        />
      </svg>

      {/* Percentage inside the circle */}
      <Typography
        sx={{
          position: "absolute",
          left: 0,
          right: 0,
          top: "50%",
          transform: "translateY(-55%)",
          textAlign: "center",
          fontWeight: 700,
          fontSize: "24px",
          color,
        }}
      >
        {Math.round(percentage)}
      </Typography>

      {/* Label under percentage (small) */}
      <Typography
        sx={{
          position: "absolute",
          left: 0,
          right: 0,
          top: "68%",
          transform: "translateY(-50%)",
          textAlign: "center",
          fontSize: "12px",
          color: "text.secondary",
          fontWeight: 600,
        }}
      >
        %
      </Typography>
    </Box>
  );
}

const TestScoreComputationPage = () => {
  const navigate = useNavigate();
  // const { projectId } = useParams();

  // ======= State hooks =======
  const [loading, setLoading] = useState(true);
  const [computing, setComputing] = useState(false);
  const [error, setError] = useState(null);
  const [scoreData, setScoreData] = useState(null);

  // Redux state
  const projectId = useSelector((state) => state.project.currentProjectId);

  // Fetch and compute trust score on component mount
  // Default to "All" so table shows everything on first load
  const [selectedRisk, setSelectedRisk] = useState("All");
  const [selectedMetric, setSelectedMetric] = useState("All");
  const [selectedOutcome, setSelectedOutcome] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");

  // ======= Fetching hook =======
  useEffect(() => {
    let isMounted = true;

    const computeTrustScore = async () => {
      if (!projectId) {
        console.log("No projectId, skipping computation");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setComputing(true);
        setError(null);

        // First, trigger the computation
        const computeResponse = await axios.post(
          `${baseUrl}/test-score-computation`,
          {
            project_id: projectId,
          }
        );

        if (!isMounted) return;
        if (computeResponse.data?.success) {
          // Ensure we accept both possible naming conventions from backend.
          // Some earlier code references `unfiedTrustMatrix` (typo). We'll standardize.
          const data = computeResponse.data.data || {};
          // normalize unified matrix key
          if (data.unifiedTrustMatrix && !data.unfiedTrustMatrix) {
            data.unfiedTrustMatrix = data.unifiedTrustMatrix;
          }
          setScoreData(data);
        } else {
          setError(
            computeResponse.data.error || "Failed to compute trust score"
          );
        }
      } catch (err) {
        if (!isMounted) return;
        console.error("Error computing trust score:", err);
        setError(
          err.response?.data?.error ||
            "Failed to compute trust score. Please run test evaluation first."
        );
      } finally {
        if (!isMounted) return;
        setLoading(false);
        setComputing(false);
      }
    };

    computeTrustScore();
  }, [projectId]);

  const dispatch = useDispatch();

  // Redux state
  const computedScores = useSelector((state) => state.testScore.computedScores);
  const evaluationResults = useSelector(
    (state) => state.testScore.evaluationResults
  );
  const trustAttributes = useSelector(
    (state) => state.testScore.trustAttributes
  );
  // const loading = useSelector((state) => state.testScore.loading);
  // const error = useSelector((state) => state.testScore.error);

  // Local UI state (not persisted)
  const [activeTab, setActiveTab] = useState(0);
  const [search, setSearch] = useState("");
  const [riskFilter, setRiskFilter] = useState("");
  const [metricFilter, setMetricFilter] = useState("");
  const [outcomeFilter, setOutcomeFilter] = useState("");

  // Fetch test score data from database on mount
  useEffect(() => {
    const fetchTestScoreData = async () => {
      if (!projectId) {
        console.log("No projectId, skipping fetch");
        return;
      }

      try {
        dispatch(setTestScoreLoading(true));
        const baseUrl =
          process.env.REACT_APP_API_URL || "https://validaite.io/api";
        const response = await axios.get(
          `${baseUrl}/test-score-computation?project_id=${projectId}`
        );

        if (response.data.success && response.data.data) {
          console.log("Fetched test score data:", response.data.data);
          dispatch(setTestScoreData(response.data.data));
        }
      } catch (err) {
        console.error("Error fetching test score data:", err);
        dispatch(setTestScoreError("Failed to load data"));
      } finally {
        dispatch(setTestScoreLoading(false));
      }
    };

    fetchTestScoreData();
  }, [projectId, dispatch]);

  // ======= Derived data (safe when scoreData is null) =======
  // const overallTrustIndex = scoreData?.overallTrustIndex ?? 0;
  // const targetTrustIndex = scoreData?.targetTrustIndex ?? 75;
  // const testSummary = scoreData?.testSummary ?? {};
  // const complianceStatus = scoreData?.complianceStatus ?? "Compliant";

  // Extract data
  const overallTrustIndex = scoreData?.overallTrustIndex || 0;
  const targetTrustIndex = scoreData?.targetTrustIndex || 89;
  const testSummary = scoreData?.testSummary || {};
  const complianceStatus = scoreData?.complianceStatus || "Compliant";
  const unfiedTrustMatrix = scoreData?.unfiedTrustMatrix || [];

  // Accept multiple naming variants from backend
  const unifiedMatrix =
    scoreData?.unfiedTrustMatrix ?? scoreData?.unifiedTrustMatrix ?? [];
  // const risks = scoreData?.risks ?? [];
  // const metrics = scoreData?.metrics ?? [];

  // Determine risk level based on trust index
  let riskLevel = "Low Risk";
  if (overallTrustIndex < 50) riskLevel = "High Risk";
  else if (overallTrustIndex < 75) riskLevel = "Medium Risk";

  const complianceColor =
    complianceStatus === "Compliant" ? "#4CAF50" : "#f44336";

  // ======= Filtered matrix (useMemo used BEFORE any early return) =======
  const filteredMatrix = useMemo(() => {
    const q = (searchQuery || "").trim().toLowerCase();

    return unifiedMatrix.filter((row) => {
      // Validate row fields existence
      if (!row) return false;

      // Attribute/Risk filter - 'All' means no filter
      // Check both attribute and risk fields for compatibility
      if (selectedRisk !== "All") {
        const rowAttribute = row.attribute || row.risk;
        if (rowAttribute !== selectedRisk) return false;
      }

      // Metric filter
      if (
        selectedMetric !== "All" &&
        selectedMetric !== "Select" &&
        row.metric !== selectedMetric
      )
        return false;

      // Outcome filter
      if (selectedOutcome !== "All" && row.finalOutcome !== selectedOutcome)
        return false;

      // Search across key fields
      if (q) {
        const fields = [
          row.attribute ?? "",
          row.useCase ?? "",
          row.risk ?? "",
          row.metric ?? "",
          row.decision ?? "",
          row.finalOutcome ?? "",
        ];
        const anyMatch = fields.some((f) =>
          f.toString().toLowerCase().includes(q)
        );
        if (!anyMatch) return false;
      }

      return true;
    });
  }, [
    unifiedMatrix,
    selectedRisk,
    selectedMetric,
    selectedOutcome,
    searchQuery,
  ]);

  // ======= Early returns (loading / error) AFTER hooks =======
  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
          bgcolor: "#fafafa",
        }}
      >
        <Box textAlign="center">
          <CircularProgress />
          <Typography mt={2} color="text.secondary">
            {computing ? "Computing Trust Score..." : "Loading..."}
          </Typography>
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ bgcolor: "#fafafa", minHeight: "100vh", p: 3 }}>
        <Typography variant="h5" fontWeight={600} mb={2}>
          Evidence & Trust Matrix
        </Typography>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
        <Box display="flex" gap={2}>
          <Button variant="contained" onClick={() => window.location.reload()}>
            Retry
          </Button>
          <Button
            variant="outlined"
            onClick={() => navigate(`/project/${projectId}/test-evaluation`)}
          >
            Go to Test Evaluation
          </Button>
        </Box>
      </Box>
    );
  }

  // Extract unique attributes/risks and metrics from trust matrix
  // For VISION, prioritize 'attribute' field; for RAG, use 'risk' field
  const risks = [...new Set(unfiedTrustMatrix.map((t) => t.attribute || t.risk))].filter(
    Boolean
  );
  const metrics = [...new Set(unfiedTrustMatrix.map((t) => t.metric))].filter(
    Boolean
  );
  const outcomes = [
    ...new Set(unfiedTrustMatrix.map((t) => t.finalOutcome)),
  ].filter(Boolean);

  const handleDownloadPDF = () => {
    try {
      const doc = new jsPDF("p", "pt", "a4");
      doc.setFontSize(16);
      doc.text("Unified Trust Matrix", 40, 40);
      doc.setFontSize(10);

      // Filter trust matrix based on selections
      const filteredMatrix = unfiedTrustMatrix.filter((row) => {
        if (selectedRisk !== "Select" && row.risk !== selectedRisk)
          return false;
        if (selectedMetric !== "Select" && row.metric !== selectedMetric)
          return false;
        if (
          selectedOutcome !== "Select" &&
          row.finalOutcome !== selectedOutcome
        )
          return false;
        return true;
      });
      const colX = [40, 140, 260, 360, 440, 500];
      // Header
      let y = 70;
      doc.setFontSize(9);
      doc.text("Attribute", colX[0], y);
      doc.text("Metrics", colX[1], y);
      doc.text("Avg Score", colX[2], y);
      doc.text("Decision", colX[3], y);
      doc.text("Final Outcome", colX[4], y);

      y += 18;

      filteredMatrix.forEach((row, idx) => {
        // wrap lengthy values to multiple lines if needed (simple approach)
        const lineHeight = 12;
        const maxY = 760;

        doc.setFontSize(9);
        doc.text(String(row.attribute || row.useCase || row.risk || ""), colX[0], y);
        doc.text(String(row.metric ?? ""), colX[1], y);
        doc.text(
          String(
            typeof row.score === "number"
              ? `${(row.score * 100).toFixed(1)}%`
              : row.score ?? ""
          ),
          colX[2],
          y
        );
        // doc.text(String(row.status ?? ""), colX[4], y);
        doc.text(String(row.decision ?? ""), colX[3], y);
        doc.text(String(row.finalOutcome ?? ""), colX[4], y);

        y += lineHeight;

        // new page if required
        if (y > maxY) {
          doc.addPage();
          y = 40;
        }
      });

      doc.save("Unified_Trust_Matrix.pdf");
    } catch (err) {
      console.error("PDF export failed:", err);
      alert("Failed to generate PDF.");
    }
  };

  const handleDownloadDOCX = async () => {
    try {
      // Build docx table rows
      const headerRow = new DocxTableRow({
        children: [
          new DocxTableCell({
            children: [new Paragraph({ children: [new TextRun("Attribute")] })],
          }),
          new DocxTableCell({
            children: [new Paragraph({ children: [new TextRun("Metrics")] })],
          }),
          new DocxTableCell({
            children: [new Paragraph({ children: [new TextRun("Avg Score")] })],
          }),
          // new DocxTableCell({
          //   children: [new Paragraph({ children: [new TextRun("Status")] })],
          // }),
          new DocxTableCell({
            children: [new Paragraph({ children: [new TextRun("Decision")] })],
          }),
          new DocxTableCell({
            children: [
              new Paragraph({ children: [new TextRun("Final Outcome")] }),
            ],
          }),
        ],
      });

      const bodyRows = filteredMatrix.map(
        (row) =>
          new DocxTableRow({
            children: [
              new DocxTableCell({
                children: [new Paragraph(String(row.attribute || row.useCase || row.risk || ""))],
              }),
              new DocxTableCell({
                children: [new Paragraph(String(row.metric ?? ""))],
              }),
              new DocxTableCell({
                children: [
                  new Paragraph(
                    String(
                      typeof row.score === "number"
                        ? `${(row.score * 100).toFixed(1)}%`
                        : row.score ?? ""
                    )
                  ),
                ],
              }),
              // new DocxTableCell({
              //   children: [new Paragraph(String(row.status ?? ""))],
              // }),
              new DocxTableCell({
                children: [new Paragraph(String(row.decision ?? ""))],
              }),
              new DocxTableCell({
                children: [new Paragraph(String(row.finalOutcome ?? ""))],
              }),
            ],
          })
      );

      const docTable = new DocxTable({
        rows: [headerRow, ...bodyRows],
      });

      const doc = new Document({
        sections: [
          {
            properties: {},
            children: [
              new Paragraph({
                text: "Unified Trust Matrix",
                heading: "Heading1",
              }),
              docTable,
            ],
          },
        ],
      });

      const blob = await Packer.toBlob(doc);
      saveAs(blob, "Unified_Trust_Matrix.docx");
    } catch (err) {
      console.error("DOCX export failed:", err);
      alert("Failed to generate DOCX.");
    }
  };

  // ======= Rendered UI =======
  return (
    <ProjectRequired>
      <Box sx={{ backgroundColor: "#fafafa", minHeight: "100vh" }}>
        {/* ===== Page Title (Sticky Header) ===== */}
        <Box
          sx={{
            position: "sticky",
            top: 50,
            zIndex: 100,
            backgroundColor: "rgba(250, 250, 250, 0.95)",
            backdropFilter: "blur(6px)",
            padding: "24px 32px 16px 32px",
            borderBottom: "1px solid #e0e0e0",
          }}
        >
          <Typography variant="h5" fontWeight={700} sx={{ color: "#1a1a1a" }}>
            Computed Trust Score
          </Typography>

          <Typography variant="body2" color="text.secondary" mt={0.5}>
            Composite AI Trustworthiness Scoring & Risk Posture Index
          </Typography>
        </Box>

        {/* Main Content Area */}
        <Box sx={{ padding: "24px 32px" }}>
          {/* ===== Trust Matrix Card (redesigned: 3 columns + dropdowns below) ===== */}
          <Card
            elevation={0}
            sx={{
              padding: "24px",
              borderRadius: "12px",
              border: "1px solid #e0e0e0",
              mb: 3,
              backgroundColor: "#ffffff",
            }}
          >
            <Typography
              variant="h6"
              fontWeight={600}
              mb={3}
              sx={{ color: "#1a1a1a", fontSize: "18px" }}
            >
              Trust Matrix
            </Typography>

            {/* 3 horizontal blocks */}
            <Grid container spacing={3}>
              {/* Trust Matrix Block */}
              <Grid item xs={12} md={4}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    borderRadius: "12px",
                    border: "1px solid #e0e0e0",
                    height: "100%",
                    position: "relative",
                  }}
                >
                  {/* Risk badge - top-right */}
                  <Chip
                    label={riskLevel}
                    size="small"
                    sx={{
                      position: "absolute",
                      top: 14,
                      right: 14,
                      backgroundColor:
                        riskLevel === "Low Risk"
                          ? "#4CAF50"
                          : riskLevel === "Medium Risk"
                          ? "#ff9800"
                          : "#f44336",
                      color: "#fff",
                      fontWeight: 600,
                      fontSize: "12px",
                      px: 1,
                    }}
                  />

                  <Box
                    sx={{ display: "flex", justifyContent: "center", mt: 2 }}
                  >
                    <CompactCircularGauge
                      percentage={Math.round(overallTrustIndex)}
                      size={140}
                    />
                  </Box>

                  <Box sx={{ textAlign: "center", mt: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      Overall Trust Index
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: "text.secondary", mt: 0.5 }}
                    >
                      {testSummary?.archetype ?? ""}
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      mt: 2,
                      display: "flex",
                      justifyContent: "center",
                      gap: 1,
                    }}
                  >
                    {/* <Typography
                      variant="caption"
                      sx={{ color: "text.secondary" }}
                    >
                      Target:
                    </Typography> */}
                    {/* <Typography variant="caption" sx={{ fontWeight: 700 }}>
                      {targetTrustIndex}
                    </Typography> */}
                  </Box>
                </Paper>
              </Grid>

              {/* Test Summary Block */}
              <Grid item xs={12} md={4}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    borderRadius: "12px",
                    border: "1px solid #e0e0e0",
                    height: "100%",
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 600, mb: 2 }}
                  >
                    Test Summary
                  </Typography>

                  <Box sx={{ mb: 2 }}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: "block" }}
                    >
                      Total Runs
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      {testSummary?.totalTests ?? 0}
                    </Typography>
                  </Box>

                  <Box sx={{ mb: 2 }}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: "block" }}
                    >
                      Use Cases
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      {testSummary?.totalUseCases ?? 1}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: "block" }}
                    >
                      Archetype
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      {testSummary?.archetype ?? "Unknown"}
                    </Typography>
                  </Box>
                </Paper>
              </Grid>

              {/* Compliance Block */}
              {/* <Grid item xs={12} md={4}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    borderRadius: "12px",
                    border: "1px solid #e0e0e0",
                    height: "100%",
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 600, mb: 2 }}
                  >
                    Compliance
                  </Typography>

                  <Chip
                    label={complianceStatus}
                    size="small"
                    sx={{
                      backgroundColor: complianceColor,
                      color: "#fff",
                      fontWeight: 600,
                      fontSize: "12px",
                      mb: 2,
                    }}
                  />

                  <Box sx={{ mt: 1.5 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        color: complianceColor,
                        display: "flex",
                        alignItems: "center",
                        mb: 1,
                      }}
                    >
                      ✓&nbsp;NIST RMF Covered
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{
                        color: complianceColor,
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      ✓&nbsp;EU AI Act Articles
                    </Typography>
                  </Box>
                </Paper>
              </Grid> */}
            </Grid>

            {/* Dropdowns row below the 3 blocks */}
            <Box sx={{ mt: 4 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    mb={1}
                    display="block"
                    sx={{ fontSize: "12px" }}
                  >
                    Attributes
                  </Typography>
                  <Select
                    fullWidth
                    size="small"
                    value={selectedRisk}
                    onChange={(e) => setSelectedRisk(e.target.value)}
                    sx={{
                      backgroundColor: "#fff",
                      borderRadius: "6px",
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#e0e0e0",
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#0094fb",
                      },
                    }}
                  >
                    <MenuItem value="All">All</MenuItem>
                    {risks.map((risk, idx) => (
                      <MenuItem key={`risk-${idx}`} value={risk}>
                        {risk}
                      </MenuItem>
                    ))}
                  </Select>
                </Grid>

                <Grid item xs={12} md={4}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    mb={1}
                    display="block"
                    sx={{ fontSize: "12px" }}
                  >
                    Metrics
                  </Typography>
                  <Select
                    fullWidth
                    size="small"
                    value={selectedMetric}
                    onChange={(e) => setSelectedMetric(e.target.value)}
                    sx={{
                      backgroundColor: "#fff",
                      borderRadius: "6px",
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#e0e0e0",
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#0094fb",
                      },
                    }}
                  >
                    <MenuItem value="All">All</MenuItem>
                    {metrics.map((metric, idx) => (
                      <MenuItem key={`metric-${idx}`} value={metric}>
                        {metric}
                      </MenuItem>
                    ))}
                  </Select>
                </Grid>

                <Grid item xs={12} md={4}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    mb={1}
                    display="block"
                    sx={{ fontSize: "12px" }}
                  >
                    Outcomes
                  </Typography>
                  <Select
                    fullWidth
                    size="small"
                    value={selectedOutcome}
                    onChange={(e) => setSelectedOutcome(e.target.value)}
                    sx={{
                      backgroundColor: "#fff",
                      borderRadius: "6px",
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#e0e0e0",
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#0094fb",
                      },
                    }}
                  >
                    <MenuItem value="All">All</MenuItem>
                    <MenuItem value="Trusted">Trusted</MenuItem>
                    <MenuItem value="Needs Review">Needs Review</MenuItem>
                    <MenuItem value="Pass">Pass</MenuItem>
                    <MenuItem value="Fail">Fail</MenuItem>
                  </Select>
                </Grid>
              </Grid>
            </Box>
          </Card>

          {/* ===== Unified Trust Matrix Table ===== */}
          <Card
            elevation={0}
            sx={{
              padding: "20px",
              borderRadius: "12px",
              border: "1px solid #e0e0e0",
              mb: 3,
              backgroundColor: "#ffffff",
            }}
          >
            <Grid container alignItems="center" spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography
                  variant="h6"
                  fontWeight={600}
                  sx={{ color: "#1a1a1a", fontSize: "18px" }}
                >
                  Trust Matrix ({filteredMatrix.length} rows)
                </Typography>
              </Grid>

              <Grid
                item
                xs={12}
                md={6}
                sx={{ textAlign: { xs: "left", md: "right" } }}
              >
                <TextField
                  size="small"
                  placeholder="Search"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton size="small" aria-label="search">
                          <SearchIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{ width: { xs: "100%", md: 320 } }}
                />
              </Grid>
            </Grid>

            <Box sx={{ mt: 2 }}>
              <TableContainer
                component={Paper}
                sx={{
                  boxShadow: "none",
                  border: "1px solid #e0e0e0",
                  borderRadius: "8px",
                  overflow: "auto",
                }}
              >
                <Table size="small">
                  <TableHead sx={{ backgroundColor: "#f8f9fa" }}>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Attribute</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Metrics</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Avg Score</TableCell>
                      {/* <TableCell sx={{ fontWeight: 600 }}>Status</TableCell> */}
                      <TableCell sx={{ fontWeight: 600 }}>Decision</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>
                        Final Outcome
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredMatrix.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                          <Typography
                            color="text.secondary"
                            sx={{ fontSize: "14px" }}
                          >
                            No data available
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredMatrix.map((row, idx) => (
                        <TableRow
                          key={`row-${idx}`}
                          hover
                          sx={{
                            "&:hover": { backgroundColor: "#f8f9fa" },
                            borderBottom: "1px solid #f0f0f0",
                          }}
                        >
                          <TableCell
                            sx={{ fontSize: "13px", color: "#1a1a1a", py: 2, fontWeight: 600 }}
                          >
                            {row.attribute || row.useCase || row.risk}
                          </TableCell>
                          <TableCell
                            sx={{ fontSize: "13px", color: "#1a1a1a", py: 2 }}
                          >
                            {row.metric}
                          </TableCell>
                          <TableCell
                            sx={{
                              fontSize: "13px",
                              color: "#1a1a1a",
                              py: 2,
                              fontWeight: 600,
                            }}
                          >
                            {typeof row.score === "number"
                              ? `${(row.score * 100).toFixed(1)}%`
                              : row.score}
                          </TableCell>
                          {/* <TableCell sx={{ py: 2 }}>
                            <Chip
                              label={row.status}
                              size="small"
                              sx={{
                                backgroundColor:
                                  row.status === "Pass" || row.status === "PASS" ? "#d4edda" : "#f8d7da",
                                color:
                                  row.status === "Pass" || row.status === "PASS" ? "#155724" : "#721c24",
                                fontWeight: 600,
                                fontSize: "11px",
                                border:
                                  row.status === "Pass" || row.status === "PASS"
                                    ? "1px solid #c3e6cb"
                                    : "1px solid #f5c6cb",
                              }}
                            />
                          </TableCell> */}
                          <TableCell
                            sx={{ fontSize: "13px", color: "#1a1a1a", py: 2 }}
                          >
                            {row.decision}
                          </TableCell>
                          <TableCell sx={{ py: 2 }}>
                            <Chip
                              label={row.finalOutcome}
                              size="small"
                              sx={{
                                backgroundColor:
                                  row.finalOutcome === "Trusted"
                                    ? "#d4edda"
                                    : "#fff3cd",
                                color:
                                  row.finalOutcome === "Trusted"
                                    ? "#155724"
                                    : "#856404",
                                fontWeight: 600,
                                fontSize: "11px",
                                border:
                                  row.finalOutcome === "Trusted"
                                    ? "1px solid #c3e6cb"
                                    : "1px solid #ffeaa7",
                              }}
                            />
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Card>
          {/* ===== Footer Buttons (Back / Save / Dashboard) ===== */}
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            gap={2}
            mt={4}
            mb={2}
          >
            {/* Back Button */}
            <Button
              variant="outlined"
              onClick={() => navigate(`/project/${projectId}/test-evaluation`)}
              sx={{
                textTransform: "none",
                borderRadius: "8px",
                borderColor: "#0094fb",
                color: "#0094fb",
                px: 3,
                py: 1.5,
                fontSize: "14px",
                fontWeight: 600,
                "&:hover": {
                  borderColor: "#0077cc",
                  backgroundColor: "#f0f8ff",
                },
              }}
            >
              ← Back to Test Evaluation
            </Button>

            {/* Right Side Buttons (Save + Dashboard) */}
            <Box display="flex" gap={2}>
              {/* Save Draft → DOCX download */}
              <Button
                variant="outlined"
                startIcon={<DownloadSVG style={{ width: 18, height: 18 }} />}
                onClick={handleDownloadDOCX}
                sx={{
                  textTransform: "none",
                  borderRadius: "8px",
                  borderColor: "#0094fb",
                  color: "#0094fb",
                  px: 3,
                  py: 1.5,
                  fontSize: "14px",
                  fontWeight: 600,
                  "&:hover": {
                    borderColor: "#0077cc",
                    backgroundColor: "#f0f8ff",
                  },
                }}
              >
                Download File
              </Button>

              {/* Dashboard Button */}
              <Button
                variant="contained"
                onClick={() =>
                  navigate(`/project/${projectId}/evaluation-dashboard`)
                }
                sx={{
                  backgroundColor: "#0094fb",
                  "&:hover": { backgroundColor: "#0077cc" },
                  textTransform: "none",
                  borderRadius: "8px",
                  px: 3,
                  py: 1.5,
                  fontSize: "14px",
                  fontWeight: 600,
                  boxShadow: "none",
                  "&:hover": {
                    backgroundColor: "#0077cc",
                    boxShadow: "0 2px 8px rgba(0, 148, 251, 0.3)",
                  },
                }}
              >
                Go to Dashboard →
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>
    </ProjectRequired>
  );
};

export default TestScoreComputationPage;
